import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Clock, RefreshCw, Send, CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString()}`;

const FollowUpTab = () => {
  const [data, setData] = useState({ quotes: [], stats: { open: 0, need_follow_up: 0, confirmed: 0, declined: 0, total: 0, conversion_rate: 0 } });
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [followUpNote, setFollowUpNote] = useState('');

  const load = () => { fetch(`${API}/api/command-center/follow-ups`).then(r => r.json()).then(setData).catch(() => {}); };
  useEffect(load, []);

  const updateQuote = async (id, updates) => {
    await fetch(`${API}/api/command-center/quote-log/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
    load();
  };

  const filtered = filter === 'all' ? data.quotes : data.quotes.filter(q => q.status === filter);
  const { stats } = data;

  return (
    <div data-testid="follow-up-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" /> Follow-Up Tracker</h2>
        <Button variant="outline" size="sm" onClick={load} className="text-xs h-8"><RefreshCw className="h-3 w-3 mr-1" /> Refresh</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-blue-600">{stats.open}</p><p className="text-[10px] text-gray-500">Open Quotes</p></Card>
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-amber-600">{stats.need_follow_up}</p><p className="text-[10px] text-gray-500">Need Follow-Up</p></Card>
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-emerald-600">{stats.conversion_rate}%</p><p className="text-[10px] text-gray-500">Conversion Rate</p></Card>
        <Card className="p-3 border-gray-200 text-center">
          <div className="flex justify-center gap-3 text-[10px]">
            <span className="text-emerald-600 font-bold">{stats.confirmed} confirmed</span>
            <span className="text-red-600 font-bold">{stats.declined} declined</span>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">Outcomes</p>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-1 mb-3">
        {['all', 'sent', 'pending', 'confirmed', 'declined'].map(f2 => (
          <button key={f2} onClick={() => setFilter(f2)} className={`px-3 py-1 rounded-full text-[10px] font-bold capitalize ${filter === f2 ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{f2}</button>
        ))}
      </div>

      {/* Conversion Overview */}
      {stats.total > 0 && (
        <Card className="p-3 border-gray-200 mb-4">
          <h4 className="text-[10px] font-bold text-gray-400 uppercase mb-2">Conversion Overview</h4>
          <div className="flex gap-2 h-4 rounded-full overflow-hidden bg-gray-100">
            {stats.confirmed > 0 && <div style={{ width: `${(stats.confirmed / stats.total) * 100}%` }} className="bg-emerald-500 rounded-full" />}
            {(stats.open) > 0 && <div style={{ width: `${(stats.open / stats.total) * 100}%` }} className="bg-amber-400 rounded-full" />}
            {stats.declined > 0 && <div style={{ width: `${(stats.declined / stats.total) * 100}%` }} className="bg-red-500 rounded-full" />}
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-gray-500">
            <span className="text-emerald-600">Confirmed {stats.confirmed}</span>
            <span className="text-amber-600">Open {stats.open}</span>
            <span className="text-red-600">Declined {stats.declined}</span>
          </div>
        </Card>
      )}

      {/* Quote Pipeline */}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center border-gray-200">
          <Clock className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No quotes in this category.</p>
          <p className="text-[10px] text-gray-400 mt-1">Log quotes from Quick Quote or Dispatch to track follow-ups.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(q => {
            const isExpanded = expandedId === q.quote_id;
            return (
              <Card key={q.quote_id} className="border-gray-200 overflow-hidden">
                <button className="w-full p-3 text-left flex items-center justify-between" onClick={() => setExpandedId(isExpanded ? null : q.quote_id)}>
                  <div className="flex items-center gap-2 flex-1">
                    {q.status === 'confirmed' ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : q.status === 'declined' ? <XCircle className="h-4 w-4 text-red-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-900">{q.client_name || q.quote_id}</span>
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold capitalize ${q.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : q.status === 'declined' ? 'bg-red-100 text-red-700' : q.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{q.status}</span>
                      </div>
                      <div className="flex gap-3 text-[10px] text-gray-500 mt-0.5">
                        <span>{q.vehicle_name}</span><span>{q.hours}hrs</span><span>{q.created_at?.slice(0, 10)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-red-600">{f(q.grand_total || q.total || 0)}</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 pt-0 border-t border-gray-100">
                    <div className="grid sm:grid-cols-2 gap-3 mt-3">
                      {/* Actions */}
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Update Status</p>
                        <div className="flex gap-1 flex-wrap">
                          {['sent', 'pending', 'confirmed', 'declined'].map(s => (
                            <button key={s} onClick={() => updateQuote(q.quote_id, { status: s })}
                              className={`px-2 py-1 rounded text-[10px] font-bold capitalize border ${q.status === s ? 'bg-black text-white border-black' : 'border-gray-200 hover:border-gray-300'}`}>{s}</button>
                          ))}
                        </div>
                      </div>
                      {/* Follow-up Note */}
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Follow-Up Note</p>
                        <div className="flex gap-1">
                          <Input placeholder="Add note..." value={followUpNote} onChange={e => setFollowUpNote(e.target.value)} className="h-7 text-[10px] flex-1" />
                          <Button size="sm" onClick={() => { updateQuote(q.quote_id, { follow_up_notes: followUpNote, status: 'pending', follow_up_date: new Date().toISOString() }); setFollowUpNote(''); }} className="h-7 text-[10px] bg-amber-600 hover:bg-amber-700 px-2"><Send className="h-3 w-3" /></Button>
                        </div>
                        {q.follow_up_notes && <p className="text-[10px] text-gray-500 mt-1 italic">Last note: {q.follow_up_notes}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FollowUpTab;
